import type { InferType, Selection, TypeFromSelection } from 'groqd'
import { q } from 'groqd'
import type { ZodRawShape } from 'zod'

import { Language } from '@nxt-insurance/constants'

import { colorUnion, getSlugsQuery, imageAsset } from '@/cms/helperTypes'
import { multiColumnSectionSelection, sectionsQuery } from '@/cms/sections'
import { locales } from '@/constants'
import { getLocaleParts } from '@/utils/locale'

import { webUrlSelector } from './webUrl'

const ctaItemSelection = {
  _type: q.literal('website.richContent.ctaItem'),
  label: ['label[$lang]', q.string()],
  productLabel: ['productLabel[$lang]', q.string()],
  target: webUrlSelector('target').nullable(),
} satisfies Selection

const pageTypeQuery = (name: string) =>
  q(name)
    .filter()
    .select({
      '_type == "website.pageType.bundleProductPage"': {
        _type: q.literal('website.pageType.bundleProductPage'),
        bundleName: q.slug('bundleName'),
        flowName: q('buyingFlow').deref().grabOne('slug.current', q.string()),
      },
      '_type == "website.pageType.productPage"': {
        _type: q.literal('website.pageType.productPage'),
        abstractProduct: q('abstractProduct')
          .deref()
          .grab({
            defaultProduct: q('defaultProduct')
              .deref()
              .grab({
                _id: q.string(),
                productKey: q('productKey')
                  .deref()
                  .grab({
                    productKey: ['productKey.current', q.string()],
                  }),
                faqs: q('faqs')
                  .filter()
                  .grab({
                    _key: q.string(),
                    heading: ['heading[$lang]', q.string()],
                    description: ['description[$lang]', q.string().nullable()],
                  })
                  .nullable(),
              }),
            abstractProductKey: ['abstractProductKey.current', q.string()],
            webBuyingFlow: q('webBuyingFlow')
              .deref()
              .grabOne('slug.current', q.string().nullable()),
          }),
      },
      '_type == "website.pageType.mgaLightProductPage"': {
        _type: q.literal('website.pageType.mgaLightProductPage'),
        mgaLightProduct: q('mgaLightProduct')
          .deref()
          .grab({
            mgaLightProductKey: ['mgaLightProductKey.current', q.string()],
            appointmentLink: ['(appointmentLinkV2[_key == $lang].value)[0]', q.string().nullable()],
            webBuyingFlow: q('webBuyingFlow')
              .deref()
              .grabOne('slug.current', q.string().nullable()),
          }),
      },
      default: {
        _type: q.union([
          q.literal('website.pageType.defaultPage'),
          q.literal('website.pageType.indexPage'),
          q.literal('website.pageType.blogOverviewPage'),
          q.literal('website.pageType.noHeaderNoFooter'),
          q.literal('website.pageType.noTermsAndConditions'),
        ]),
      },
    })
    .slice(0)
    .nullable()

const alternativeRoutesQuery = q('*')
  .filter(
    '_type == "website.route" && _id != ^._id && !string::startsWith(_id, "draft") && slug.en.current == ^.slug.en.current && folder.en == ^.folder.en'
  )
  .grab({
    market: q.string(),
    slug: ['slug[$lang].current', q.string().nullable()],
    folder: ['folder[$lang].current', q.string().nullable()],
  })

const pageSelection = {
  _type: q.literal('website.richContent.page'),
  _id: q.string(),
  name: q.string(),
  footerColor: colorUnion.nullable(),
  sections: sectionsQuery('sections'),
  metaDescription: q('metaDescription').grab({
    title: ['title[$lang]', q.string().nullable()],
    description: ['description[$lang]', q.string().nullable()],
    metaImage: q('metaImage')
      .grab({
        _type: q.literal('image'),
        asset: q('asset').deref().grab(imageAsset),
      })
      .nullable(),
  }),
} satisfies Selection

export const pageQuery = q('*')
  .filter('_type == "website.richContent.page" && _id == $id')
  .grab(pageSelection)
  .slice(0)

const pageOrAbTestQuery = (name: string) =>
  q(name)
    .deref()
    .select({
      '_type == "website.richContent.page"': pageSelection,
      '_type == "website.richContent.page.ab.test"': {
        _type: q.literal('website.richContent.page.ab.test'),
        _id: q.string(),
        title: q.string(),
        featureFlag: q.string(),
        variations: q.array(
          q.object({
            name: q.string(),
            page: q.object({
              _type: q.literal('reference'),
              _ref: q.string(),
            }),
          })
        ),
      },
      default: { _type: q.string() },
    })

export const allRoutesQuery = q('*')
  .filter(
    '_type == "website.route" && market == $market && defined(slug[$lang].current) && defined(availableEnvironments) && redirectActive != true'
  )
  .grab({
    slug: ['slug[$lang].current', q.string()],
    folder: ['folder[$lang]', q.string().nullable()],
    locale: ['$lang + "-" + $market', q.string()],
    redirectActive: q.boolean().nullable(),
    redirectTarget: q.string().nullable(),
  })

export const allRoutesWithSlug = () => {
  const slugConditions = Object.values(Language).map((lang) => `slug.${lang}.current == $slug`)
  return q('*')
    .filter(`_type == "website.route" && (${slugConditions.join(' || ')})`)
    .grab({
      market: q.string(),
    })
}

const getFoldersQuery = () => {
  const folders: ZodRawShape = {}
  locales.forEach((locale) => {
    const [lang] = getLocaleParts(locale)
    folders[lang] = q.string().optional()
  })
  return folders
}

const routeSelection = {
  market: q.string(),
  slug: ['slug[$lang].current', q.string()],
  slugs: ['slug', q.object(getSlugsQuery())],
  folder: ['folder[$lang]', q.string().nullable()],
  folders: ['folder', q.object(getFoldersQuery()).nullable()],
  availableEnvironments: q.array(q.union([q.literal('staging'), q.literal('production')])),
  redirectActive: q.boolean().nullable(),
  redirectTarget: q.string().nullable(),
  disallowRobots: q.boolean().nullable(),
  alternativeRoutes: alternativeRoutesQuery,
  page: pageOrAbTestQuery('page'),
  pageType: pageTypeQuery('pageType'),
} satisfies Selection

export const routeQuery = q('*')
  .filter(
    '_type == "website.route" && market == $market && slug[$lang].current == $slug && defined(availableEnvironments) && (!defined(folder[$lang]) || folder[$lang] == $folder)'
  )
  .grab(routeSelection)
  .slice(0)
  .nullable()

export const routeQueryPreview = q('*')
  .filter(
    '_type == "website.route" && market == $market && slug[$lang].current == $slug && defined(availableEnvironments) && (!defined(folder[$lang]) || folder[$lang] == $folder)'
  )
  .grab({
    ...routeSelection,
    page: q('page')
      .deref()
      .grab(
        { _type: q.string() },
        {
          '_type == "website.richContent.page"': pageSelection,
          '_type == "website.richContent.page.ab.test"': {
            _type: q.literal('website.richContent.page.ab.test'),
            _id: q.string(),
            title: q.string(),
            featureFlag: q.string(),
            variations: q('variations')
              .filter()
              .grab({
                name: q.string(),
                page: q('page').deref().grab(pageSelection),
              }),
          },
        }
      ),
  })
  .slice(0)
  .nullable()

export const redirectQuery = q('*')
  .filter('_type == "website.redirect" && enabled == true && oldPath.current == $path')
  .grab({ newPath: q.string() })
  .slice(0)
  .nullable()

export const siteSettingsQueryFooter = q('*')
  .filter('_type == "website.siteSettings" && market == $market')
  .grab({
    footer: sectionsQuery('footer'),
  })
  .slice(0)
export const siteSettingsQuerySecondaryFooter = q('*')
  .filter('_type == "website.siteSettings" && market == $market')
  .grab({
    secondaryFooter: sectionsQuery('secondaryFooter'),
  })
  .slice(0)

const navbarSelection = {
  items: q('items')
    .filter()
    .deref()
    .select({
      '_type == "website.richContent.navbarMenuItem"': {
        _id: q.string(),
        _type: q.literal('website.richContent.navbarMenuItem'),
        title: ['title[$lang]', q.string()],
        headerMenu: q('headerMenu').grab(multiColumnSectionSelection).nullable(),
      },
      default: {
        _id: q.string(),
        _type: q.literal('website.richContent.navbarLinkItem'),
        isNew: q.union([q.boolean(), q.null()]),
        title: ['title[$lang]', q.string()],
        target: webUrlSelector('target').nullable(),
      },
    }),
  ctaItems: q('ctaItems')
    .filter()
    .deref()
    .grab({ ...ctaItemSelection, _id: q.string() })
    .nullable(),
} satisfies Selection

export const navbarQuery = q('*')
  .filter('_type == "website.navbar" && _id == $id')
  .grab(navbarSelection)
  .slice(0)

export const siteSettingsQueryNavbar = q('*')
  .filter('_type == "website.siteSettings" && market == $market')
  .grab({
    navbar: q('navbar')
      .deref()
      .select({
        '_type == "website.navbar"': {
          _type: q.literal('website.navbar'),
          _id: q.string(),
        },
        default: {
          _type: q.literal('website.navbar.ab.test'),
          _id: q.string(),
          title: q.string(),
          featureFlag: q.string(),
          variations: q('variations')
            .filter()
            .grab({
              name: q.string(),
              navbar: q('navbar')
                .deref()
                .grab({
                  _type: q.literal('website.navbar'),
                  _id: q.string(),
                }),
            }),
        },
      }),
  })
  .slice(0)

export type Page = TypeFromSelection<typeof pageSelection>
export type Route = NonNullable<InferType<typeof routeQuery>>
export type PageType = NonNullable<InferType<ReturnType<typeof pageTypeQuery>>>
export type Navbar = InferType<typeof navbarQuery>
export type NavbarItem = Navbar['items'][number]
export type SiteSettingsNavbar = InferType<typeof siteSettingsQueryNavbar>['navbar']
export type CtaItem = NonNullable<Navbar['ctaItems']>[number]
export type NavbarMenuLinkItem = NavbarItem & { _type: 'website.richContent.navbarLinkItem' }
export type RouteWithPage = Omit<Route, 'page'> & { page: Page }
